import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, ArrowRight, ShieldCheck, Plus, Trash2, ArrowLeft, Upload, ImageIcon, Lock, PlayCircle, ChevronDown, LayoutList, Sparkles, ExternalLink, X, LogOut, GripVertical, Star, Share2, RotateCcw } from 'lucide-react';
import { SupplierDataCheck, QuoteBanner, ResearchReport, PartnerInvite } from './SupplierDataCheck';
import { RichProductContent } from './ProductPage';
import useDiscount, { DiscountNote } from '../../lib/useDiscount';
import PortalReviewCard from '../../components/PortalReviewCard';
import { TermsTick } from '../../components/TermsTick';
import { StaffCountInput } from '../../components/StaffCountInput';
import { SalesCallCard } from '../../components/SalesCallCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ctc_supplier_token';

const Mark = ({ className = 'h-8 w-8' }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <clipPath id="ctc-mark-clip-sc"><rect width="64" height="64" rx="15" /></clipPath>
    <g clipPath="url(#ctc-mark-clip-sc)">
      <rect width="32" height="64" fill="#1A3A5F" />
      <rect x="32" width="32" height="64" fill="#00A79D" />
    </g>
    <path d="M17 33.5 L27 43.5 L47 21" stroke="#FFFFFF" strokeWidth="6.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Wordmark = () => (
  <span className="ctc-wordmark font-extrabold text-lg tracking-tight">
    <span className="text-[#1A3A5F]">care</span><span className="text-[#00A79D]">tech</span><span className="text-[#1A3A5F]">compare</span>
  </span>
);
const portalLogout = () => {
  localStorage.removeItem('ctc_supplier_token');
  sessionStorage.removeItem('ctc_demo');
  sessionStorage.removeItem('ctc_demo_tour');
  window.location.assign('/portal');
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F4F7F9]" data-testid="claim-shell">
    <div className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-2">
        <Mark /><Wordmark />
        {typeof window !== 'undefined' && localStorage.getItem('ctc_supplier_token') && (
          <button onClick={portalLogout} data-testid="portal-logout" className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 hover:border-gray-300 rounded-md px-3 py-1.5 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        )}
      </div>
    </div>
    <div className="max-w-3xl mx-auto px-4 py-10">{children}</div>
  </div>
);

// --- Drag-to-reorder primitive (native HTML5 DnD, no deps) ---
const moveItem = (arr, from, to) => { const a = [...(arr || [])]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; };

const DraggableList = ({ items, onChange, children }) => {
  const src = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const reset = () => { src.current = null; setDragging(null); setOver(null); };
  const handleProps = (i) => ({
    draggable: true,
    onDragStart: (e) => { src.current = i; setDragging(i); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (_) {} },
    onDragEnd: reset,
  });
  const dropProps = (i) => ({
    onDragOver: (e) => { e.preventDefault(); if (over !== i) setOver(i); },
    onDrop: (e) => { e.preventDefault(); const from = src.current; if (from != null && from !== i) onChange(moveItem(items, from, i)); reset(); },
  });
  return (items || []).map((item, i) => children(item, i, { handleProps: handleProps(i), dropProps: dropProps(i), isOver: over === i, isDragging: dragging === i, count: (items || []).length }));
};

const DragHandle = ({ handleProps, testid, className = '' }) => (
  <span {...handleProps} data-testid={testid} title="Drag to reorder"
    className={`cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 select-none ${className}`} style={{ touchAction: 'none' }}>
    <GripVertical className="w-4 h-4" />
  </span>
);

// First-login welcome - 3 steps to a great listing.
const OnboardingGuide = ({ listing, onJump, onDismiss }) => {
  const byKey = Object.fromEntries(richnessChecks(listing).map((c) => [c.key, c.done]));
  const steps = [
    { n: 1, title: 'Tell your story', body: 'Add your logo, a punchy tagline, an "about" and your categories so buyers know who you are.', tab: 'listing', cta: 'Start here',
      done: byKey.logo && byKey.tagline && byKey.about && byKey.categories },
    { n: 2, title: 'Show your products', body: 'Give each product rich detail - an image gallery, a video, key features and a customer testimonial.', tab: 'listing', cta: 'Add product detail',
      done: byKey.product && byKey.productdetail && (byKey.gallery || byKey.video || byKey.testimonial || byKey.faq) },
    { n: 3, title: 'Stand out & publish', body: 'Mark your best product as Featured, publish any extra products, then share your public page.', tab: 'listing', cta: 'Finish up',
      done: byKey.featured },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <div className="mt-4 rounded-xl border border-[#00A79D]/30 bg-[#00A79D]/[0.04] p-5" data-testid="portal-onboarding">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#00A79D]/15 text-[#00857D]"><Sparkles className="w-4 h-4" /></span>
          <div>
            <p className="font-heading text-lg font-semibold text-gray-900">Welcome! 3 steps to a great listing</p>
            <p className="text-sm text-gray-500">A richer listing gets more attention from care buyers - steps tick off as you go. <span className="font-semibold text-[#00857D]" data-testid="portal-onboarding-progress">{doneCount} of 3 done</span></p>
          </div>
        </div>
        <button onClick={onDismiss} data-testid="portal-onboarding-dismiss" className="text-gray-400 hover:text-gray-700 shrink-0" title="Dismiss"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {steps.map((s) => (
          <div key={s.n} className={`bg-white border rounded-lg p-4 flex flex-col transition-colors ${s.done ? 'border-emerald-300 bg-emerald-50/40' : 'border-[#E5E7EB]'}`} data-testid={`portal-onboarding-step-${s.n}`} data-done={s.done ? 'true' : 'false'}>
            {s.done
              ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white mb-2" data-testid={`portal-onboarding-step-${s.n}-check`}><Check className="w-4 h-4" /></span>
              : <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#00857D] text-white text-sm font-bold mb-2">{s.n}</span>}
            <p className={`font-semibold text-sm ${s.done ? 'text-emerald-800' : 'text-gray-900'}`}>{s.title}</p>
            <p className="text-xs text-gray-500 mt-1 flex-1">{s.body}</p>
            {s.done
              ? <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 self-start"><Check className="w-3 h-3" /> Done</span>
              : <button onClick={() => onJump(s.tab)} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#00857D] hover:underline self-start">{s.cta} <ArrowRight className="w-3 h-3" /></button>}
          </div>
        ))}
      </div>
      <button onClick={onDismiss} data-testid="portal-onboarding-got-it" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#00A79D] hover:bg-[#00857D] px-4 py-2 rounded-md transition-colors"><Check className="w-4 h-4" /> {doneCount === 3 ? 'All done - hide this' : "Got it, let's go"}</button>
    </div>
  );
};

// Pick-from-list + drag-to-rank field. Order = priority (top ranks highest in search).
export const RankedPicker = ({ testid, options, items, onChange, rankMap = {}, suggestions = [], max }) => {
  const [q, setQ] = useState('');
  const sel = items || [];
  const atMax = max != null && sel.length >= max;
  const add = (v) => { if (atMax) { setQ(''); return; } const val = (v || '').trim(); if (!val || sel.some((s) => s.toLowerCase() === val.toLowerCase())) { setQ(''); return; } onChange([...sel, val]); setQ(''); };
  const remove = (v) => onChange(sel.filter((x) => x !== v));
  const ql = q.trim().toLowerCase();
  const matches = (options || []).filter((o) => !sel.some((s) => s.toLowerCase() === o.toLowerCase()) && o.toLowerCase().includes(ql)).slice(0, 8);
  const canCustom = ql && !(options || []).some((o) => o.toLowerCase() === ql) && !sel.some((s) => s.toLowerCase() === ql);
  return (
    <div data-testid={testid} className="mt-1">
      {sel.length > 0 && (
        <div className="space-y-1.5 mb-2">
          <DraggableList items={sel} onChange={onChange}>
            {(item, i, d) => (
              <div key={item} {...d.dropProps} className={`flex items-center gap-2 bg-white border rounded-md px-2 py-1.5 transition-colors ${d.isOver ? 'border-[#00A79D] ring-1 ring-[#00A79D]/30' : 'border-[#E5E7EB]'} ${d.isDragging ? 'opacity-50' : ''}`} data-testid={`${testid}-item-${i}`}>
                {sel.length > 1 && <DragHandle handleProps={d.handleProps} testid={`${testid}-drag-${i}`} />}
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#00A79D]/10 text-[#00857D] text-[11px] font-bold shrink-0">{i + 1}</span>
                <span className="text-sm text-gray-800 flex-1 truncate">{item}</span>
                {rankMap[item] && rankMap[item].total > 1 && <span className="text-[10px] font-semibold text-[#00857D] bg-[#00A79D]/10 px-1.5 py-0.5 rounded shrink-0" title={`You rank #${rankMap[item].position} of ${rankMap[item].total} suppliers for “${item}”`} data-testid={`${testid}-rank-${i}`}>#{rankMap[item].position} of {rankMap[item].total}</span>}
                {rankMap[item] && rankMap[item].total > 1 && rankMap[item].position > 1 && i > 0 && (
                  <button type="button" onClick={() => onChange([item, ...sel.filter((x) => x !== item)])} data-testid={`${testid}-moveup-${i}`}
                    className="text-[10px] font-semibold text-[#F5871F] hover:text-[#d9740f] hover:underline shrink-0 whitespace-nowrap" title={`Move “${item}” to #1 to rank higher when buyers filter by it`}>↑ Move to #1</button>
                )}
                <button type="button" onClick={() => remove(item)} className="text-gray-400 hover:text-red-500 shrink-0" data-testid={`${testid}-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </DraggableList>
        </div>
      )}
      {!atMax && (
        <div className="relative">
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (matches.length) add(matches[0]); else if (canCustom) add(q); } }}
            placeholder="Type to search the list or add your own…" data-testid={`${testid}-input`} className="w-full border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
          {q.trim() && (matches.length > 0 || canCustom) && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E7EB] rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-options`}>
              {matches.map((o) => <button key={o} type="button" onClick={() => add(o)} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#00A79D]/5">{o}</button>)}
              {canCustom && <button type="button" onClick={() => add(q)} data-testid={`${testid}-add-custom`} className="block w-full text-left px-3 py-2 text-sm text-[#00857D] font-semibold hover:bg-[#00A79D]/5">+ Add “{q.trim()}”</button>}
            </div>
          )}
        </div>
      )}
      {!atMax && suggestions.filter((s) => !sel.some((x) => x.toLowerCase() === s.toLowerCase())).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`${testid}-suggestions`}>
          <span className="text-[11px] text-gray-400">Suggested:</span>
          {suggestions.filter((s) => !sel.some((x) => x.toLowerCase() === s.toLowerCase())).slice(0, 5).map((s) => (
            <button key={s} type="button" onClick={() => add(s)} data-testid={`${testid}-suggest-${s}`}
              className="inline-flex items-center gap-1 text-[11px] text-[#00857D] bg-[#00A79D]/8 hover:bg-[#00A79D]/16 border border-[#00A79D]/25 rounded-full px-2 py-0.5 transition-colors"><Plus className="w-2.5 h-2.5" />{s}</button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-1">{atMax ? `That's your ${max} - drag to reorder. Your #1 shows first and ranks highest when buyers filter by it. Remove one to swap.` : (max != null ? `Pick up to ${max} - drag to rank. Your #1 shows first and ranks highest when buyers filter by it.` : 'Drag to rank - your #1 shows first and ranks highest when buyers filter by it.')}</p>
    </div>
  );
};

// Profile richness meter - nudges suppliers toward a fuller, more persuasive listing.
// Shared richness checks - used by both the onboarding guide and the completeness meter.
const richnessChecks = (listing) => {
  const L = listing || {};
  const prods = L.products || [];
  const anyProd = (fn) => prods.some((p) => fn(p.rich_content || {}));
  return [
    { key: 'logo', label: 'Add your logo', tab: 'listing', done: !!L.logo_url },
    { key: 'tagline', label: 'Write a tagline', tab: 'listing', done: (L.tagline || '').trim().length > 0 },
    { key: 'about', label: 'Describe your company', tab: 'listing', done: (L.description || '').trim().length >= 40 },
    { key: 'categories', label: 'Add categories', tab: 'listing', done: (L.categories || []).length > 0 },
    { key: 'product', label: 'Add a product with a description', tab: 'listing', done: prods.some((p) => (p.description || '').trim().length > 0) },
    { key: 'productdetail', label: 'Add rich detail to a product', tab: 'listing', done: anyProd((rc) => rc.intro || (rc.sections || []).length) },
    { key: 'gallery', label: 'Upload a gallery image', tab: 'rich', done: (L.screenshots || []).length > 0 || anyProd((rc) => (rc.gallery || []).length) },
    { key: 'video', label: 'Add a video', tab: 'rich', done: (L.videos || []).length > 0 || anyProd((rc) => (rc.videos || []).length) },
    { key: 'testimonial', label: 'Add a customer testimonial', tab: 'rich', done: (L.testimonials || []).length > 0 || anyProd((rc) => (rc.testimonials || []).length) },
    { key: 'faq', label: 'Answer a common question (FAQ)', tab: 'rich', done: (L.faqs || []).length > 0 || anyProd((rc) => (rc.faqs || []).length) },
    { key: 'featured', label: 'Feature your best product', tab: 'listing', done: prods.some((p) => p.featured) },
  ];
};

const CompletenessMeter = ({ listing, onJump }) => {
  const checks = richnessChecks(listing);
  const done = checks.filter((c) => c.done).length;
  const pct = Math.round((done / checks.length) * 100);
  const todo = checks.filter((c) => !c.done).slice(0, 4);
  const tier = pct >= 100 ? { label: 'Complete', color: 'text-emerald-700', bar: 'bg-emerald-500' }
    : pct >= 70 ? { label: 'Almost there', color: 'text-[#00857D]', bar: 'bg-[#00A79D]' }
    : pct >= 40 ? { label: 'Looking good', color: 'text-[#00857D]', bar: 'bg-[#00A79D]' }
    : { label: 'Getting started', color: 'text-[#F5871F]', bar: 'bg-[#F5871F]' };
  return (
    <div className="mt-4 bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="portal-completeness">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#00A79D]" />
          <p className="text-sm font-semibold text-gray-900">Profile richness</p>
        </div>
        <p className={`text-sm font-bold ${tier.color}`} data-testid="portal-completeness-score">{pct}% · {tier.label}</p>
      </div>
      <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${tier.bar}`} style={{ width: `${pct}%` }} data-testid="portal-completeness-bar" />
      </div>
      {todo.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-2">Stand out to care buyers - add:</p>
          <div className="flex flex-wrap gap-2">
            {todo.map((c) => (
              <button key={c.key} type="button" onClick={() => onJump && onJump(c.tab)} data-testid={`portal-completeness-todo-${c.key}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#00857D] bg-[#00A79D]/8 hover:bg-[#00A79D]/15 border border-[#00A79D]/25 rounded-full px-3 py-1 transition-colors">
                <Plus className="w-3 h-3" /> {c.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid="portal-completeness-done"><Check className="w-4 h-4" /> Your listing is rich and complete - nicely done.</p>
      )}
    </div>
  );
};

export const RichList = ({ label, testid, rows, fields, onChange }) => {
  const add = () => onChange([...(rows || []), {}]);
  const upd = (i, k, v) => { const r = [...rows]; r[i] = { ...r[i], [k]: v }; onChange(r); };
  const del = (i) => onChange(rows.filter((_, x) => x !== i));
  return (
    <div data-testid={`rich-${testid}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-gray-800">{label}</label>
        <button onClick={add} data-testid={`rich-add-${testid}`} className="inline-flex items-center gap-1 text-sm text-[#00857D] font-semibold"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="space-y-3 mt-2">
        <DraggableList items={rows || []} onChange={onChange}>
          {(row, i, d) => (
            <div key={i} {...d.dropProps} className={`border rounded-lg p-3 space-y-2 bg-white transition-colors ${d.isOver ? 'border-[#00A79D] ring-1 ring-[#00A79D]/30' : 'border-[#E5E7EB]'} ${d.isDragging ? 'opacity-50' : ''}`} data-testid={`rich-${testid}-${i}`}>
              <div className="flex items-center justify-between">
                {d.count > 1 ? <DragHandle handleProps={d.handleProps} testid={`rich-${testid}-${i}-drag`} /> : <span />}
                <button onClick={() => del(i)} className="text-gray-400 hover:text-red-500 text-xs inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
              </div>
              {fields.map(([k, ph, area]) => area
                ? <textarea key={k} rows={2} placeholder={ph} value={row[k] || ''} onChange={(e) => upd(i, k, e.target.value)} className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
                : <input key={k} placeholder={ph} value={row[k] || ''} onChange={(e) => upd(i, k, e.target.value)} className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />)}
            </div>
          )}
        </DraggableList>
        {(rows || []).length === 0 && <p className="text-sm text-gray-400">None yet.</p>}
      </div>
    </div>
  );
};

// Simple editable list of plain strings (for stats and section bullets).
const StringList = ({ label, testid, items, onChange, placeholder }) => {
  const add = () => onChange([...(items || []), '']);
  const upd = (i, v) => { const r = [...(items || [])]; r[i] = v; onChange(r); };
  const del = (i) => onChange((items || []).filter((_, x) => x !== i));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-gray-700">{label}</label>
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="inline-flex items-center gap-1 text-xs text-[#00857D] font-semibold"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      <div className="space-y-2 mt-1.5">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2">
            <input value={v} placeholder={placeholder} onChange={(e) => upd(i, e.target.value)} className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            <button type="button" onClick={() => del(i)} className="text-gray-400 hover:text-red-500 px-1"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Self-service rich product-page editor (intro, stats, sections, FAQs) - same schema
// rendered on public ProductPage.js (rc.intro / rc.stats / rc.sections / rc.faqs).
export const ProductRichEditor = ({ value, onChange, testid, onDraft, productName, uploadFn }) => {
  const [open, setOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [preview, setPreview] = useState(false);
  const rc = value || {};
  const set = (patch) => onChange({ ...rc, ...patch });
  const setSection = (i, patch) => { const s = [...(rc.sections || [])]; s[i] = { ...s[i], ...patch }; set({ sections: s }); };
  const count = (rc.sections?.length || 0) + (rc.faqs?.length || 0) + (rc.videos?.length || 0)
    + (rc.gallery?.length || 0) + (rc.accordions?.length || 0) + (rc.testimonials?.length || 0);
  const runDraft = async () => {
    if (!onDraft) return;
    setDrafting(true);
    setOpen(true);
    try {
      const draft = await onDraft();
      if (draft) onChange({ ...rc, ...draft });
    } finally { setDrafting(false); }
  };
  return (
    <div className="mt-3 border-t border-dashed border-gray-100 pt-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`} className="flex-1 flex items-center justify-between text-left">
          <span className="text-sm font-semibold text-[#1A3A5F] inline-flex items-center gap-1.5"><LayoutList className="w-4 h-4 text-[#00857D]" /> Product page content - sections, media &amp; FAQs {count > 0 && <span className="text-xs font-normal text-gray-400">({count})</span>}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-500 flex-1 min-w-[220px]">Build a rich product page like our featured suppliers. Add an intro, key stats, content sections (with optional bullets) and FAQs - these appear on your public product page.</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPreview(true)} data-testid={`${testid}-preview`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A3A5F] border border-[#CBD9E3] hover:bg-[#1A3A5F]/5 px-3 py-1.5 rounded-md whitespace-nowrap">
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </button>
              {onDraft && (
                <button type="button" onClick={runDraft} disabled={drafting} data-testid={`${testid}-ai-draft`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#00A79D] hover:bg-[#00857D] disabled:opacity-60 px-3 py-1.5 rounded-md whitespace-nowrap">
                  {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {drafting ? 'Drafting from your website…' : 'Draft with AI'}
                </button>
              )}
            </div>
          </div>
          {onDraft && <p className="text-[11px] text-gray-400 -mt-2">AI writes a first draft from your website using only facts it finds there - always review and edit before saving.</p>}
          <div>
            <label className="text-xs font-semibold text-gray-700">Intro paragraph</label>
            <p className="text-[11px] text-gray-400 mb-1">One or two sentences on what this product is and who it's for. e.g. "Nourish is a digital care planning platform used by care homes and homecare teams to record person-centred care at the point of delivery."</p>
            <textarea rows={3} value={rc.intro || ''} onChange={(e) => set({ intro: e.target.value })} placeholder="A short opening paragraph about this product…" data-testid={`${testid}-intro`} className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <StringList label="Key stats (short badges)" testid={`${testid}-stats`} items={rc.stats} onChange={(stats) => set({ stats })} placeholder="e.g. 1,000+ UK care homes" />
            <p className="text-[11px] text-gray-400 mt-1">Short proof points shown as badges. e.g. "NHS Assured DSCR", "Used in 1,000+ homes", "Rated 4.8/5".</p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-700">Content sections</label>
              <button type="button" onClick={() => set({ sections: [...(rc.sections || []), { heading: '', body: '' }] })} data-testid={`${testid}-add-section`} className="inline-flex items-center gap-1 text-xs text-[#00857D] font-semibold"><Plus className="w-3.5 h-3.5" /> Add section</button>
            </div>
            <div className="space-y-3 mt-1.5">
              <DraggableList items={rc.sections || []} onChange={(sections) => set({ sections })}>
                {(sec, i, d) => (
                  <div key={i} {...d.dropProps} className={`border rounded-lg p-3 bg-white space-y-2 transition-colors ${d.isOver ? 'border-[#00A79D] ring-1 ring-[#00A79D]/30' : 'border-[#E5E7EB]'} ${d.isDragging ? 'opacity-50' : ''}`} data-testid={`${testid}-section-${i}`}>
                    {d.count > 1 && <div className="flex justify-start"><DragHandle handleProps={d.handleProps} testid={`${testid}-section-${i}-drag`} /></div>}
                    <input value={sec.heading || ''} onChange={(e) => setSection(i, { heading: e.target.value })} placeholder="Section heading - e.g. 'Key features', 'How it works', 'About us'" className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm font-medium" />
                    <textarea rows={3} value={sec.body || ''} onChange={(e) => setSection(i, { body: e.target.value })} placeholder="Describe this in plain English - what it does and the benefit to a care team." className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
                    <StringList label="Bullet points" testid={`${testid}-section-${i}-bullets`} items={sec.bullets} onChange={(bullets) => setSection(i, { bullets })} placeholder="e.g. Saves 2 hours per shift on admin" />
                    <div className="flex items-center justify-between pt-1">
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer"><input type="checkbox" checked={!!sec.boxout} onChange={(e) => setSection(i, { boxout: e.target.checked })} className="accent-[#00A79D] w-4 h-4" /> Highlight as box-out</label>
                      <button type="button" onClick={() => set({ sections: (rc.sections || []).filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500 text-xs inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                    </div>
                  </div>
                )}
              </DraggableList>
              {(rc.sections || []).length === 0 && <p className="text-xs text-gray-400">No sections yet.</p>}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-700">FAQs</label>
              <button type="button" onClick={() => set({ faqs: [...(rc.faqs || []), { q: '', a: '' }] })} data-testid={`${testid}-add-faq`} className="inline-flex items-center gap-1 text-xs text-[#00857D] font-semibold"><Plus className="w-3.5 h-3.5" /> Add FAQ</button>
            </div>
            <div className="space-y-3 mt-1.5">
              {(rc.faqs || []).map((f, i) => {
                const setFaq = (patch) => { const fq = [...(rc.faqs || [])]; fq[i] = { ...fq[i], ...patch }; set({ faqs: fq }); };
                return (
                  <div key={i} className="border border-[#E5E7EB] rounded-lg p-3 bg-white space-y-2" data-testid={`${testid}-faq-${i}`}>
                    <input value={f.q || ''} onChange={(e) => setFaq({ q: e.target.value })} placeholder="Question - e.g. 'How long does implementation take?'" className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm font-medium" />
                    <textarea rows={2} value={f.a || ''} onChange={(e) => setFaq({ a: e.target.value })} placeholder="Answer - e.g. 'Most care teams are live within 2 weeks with full onboarding support.'" className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
                    <button type="button" onClick={() => set({ faqs: (rc.faqs || []).filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500 text-xs inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                  </div>
                );
              })}
              {(rc.faqs || []).length === 0 && <p className="text-xs text-gray-400">No FAQs yet.</p>}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Image gallery</label>
            <p className="text-[11px] text-gray-400 mb-1.5">Upload screenshots or photos of your product. They appear as a gallery visitors can click to enlarge.</p>
            <GalleryUpload images={rc.gallery} onChange={(gallery) => set({ gallery })} testid={`${testid}-gallery`} label="images" uploadFn={uploadFn} />
          </div>
          <div>
            <RichList label="Videos" testid={`${testid}-videos`} rows={rc.videos || []} fields={[['title', 'Video title'], ['url', 'YouTube / Vimeo URL']]} onChange={(videos) => set({ videos })} />
            <p className="text-[11px] text-gray-400 mt-1">Paste a YouTube or Vimeo link - it embeds as a playable video on your product page.</p>
          </div>
          <div>
            <RichList label="Information accordions" testid={`${testid}-accordions`} rows={rc.accordions || []} fields={[['heading', 'Heading - e.g. Integrations, Security, Pricing'], ['body', 'Body', true]]} onChange={(accordions) => set({ accordions })} />
            <p className="text-[11px] text-gray-400 mt-1">Collapsible panels for extra detail visitors can expand.</p>
          </div>
          <div>
            <RichList label="Testimonials &amp; quotes" testid={`${testid}-testimonials`} rows={rc.testimonials || []} fields={[['quote', 'Quote', true], ['author', 'Author'], ['role', 'Role / organisation']]} onChange={(testimonials) => set({ testimonials })} />
            <p className="text-[11px] text-gray-400 mt-1">Shown as a swipeable carousel of customer quotes.</p>
          </div>
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid={`${testid}-preview-modal`} onClick={() => setPreview(false)}>
          <div className="bg-white rounded-xl max-w-3xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Preview - how visitors will see this page</span>
              <button type="button" onClick={() => setPreview(false)} data-testid={`${testid}-preview-close`} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-6">
              <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">{productName || 'Your product'}</h1>
              {(rc.intro || (rc.sections || []).length || (rc.faqs || []).length || (rc.stats || []).length || (rc.gallery || []).length || (rc.videos || []).length || (rc.accordions || []).length || (rc.testimonials || []).length)
                ? <RichProductContent rc={rc} />
                : <p className="text-gray-500 text-sm">Nothing to preview yet - add an intro, sections or FAQs above (or use “Draft with AI”).</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const authApiUpload = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

// Reusable image uploader: uploads to object storage and returns a public URL.
export const ImageUpload = ({ value, onChange, label = 'image', testid, aspect = 'aspect-video', kind = 'product', uploadFn }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Image must be under 25MB'); return; }
    setBusy(true);
    try {
      let url;
      if (uploadFn) {
        url = await uploadFn(file, kind);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await authApiUpload().post(`/supplier-listing/upload-image?kind=${kind}`, fd);
        url = data.url;
      }
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {value
        ? <div className={`${aspect} w-24 rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F1F5F4] shrink-0`}><img src={value} alt={label} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} /></div>
        : <div className={`${aspect} w-24 rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex items-center justify-center shrink-0`}><ImageIcon className="w-5 h-5 text-gray-300" /></div>}
      <div className="flex flex-col gap-1.5">
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00857D] cursor-pointer hover:text-[#00A79D]" data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {value ? `Replace ${label}` : `Upload ${label}`}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/heic,image/heif,.heic,.heif" className="hidden" onChange={pick} disabled={busy} />
        </label>
        {value && <button type="button" onClick={() => onChange('')} className="text-xs text-gray-400 hover:text-red-500 text-left">Remove</button>}
      </div>
    </div>
  );
};

/* ---------------------------------------- Claim form + checkout */
// Multi-image gallery uploader - uploads several images to object storage.
export const GalleryUpload = ({ images, onChange, testid, label = 'images', uploadFn }) => {
  const [busy, setBusy] = useState(false);
  const rows = images || [];
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is over 25MB - skipped`); continue; }
      try {
        let url;
        if (uploadFn) {
          url = await uploadFn(file, 'screenshot');
        } else {
          const fd = new FormData();
          fd.append('file', file);
          const { data } = await authApiUpload().post(`/supplier-listing/upload-image?kind=screenshot`, fd);
          url = data.url;
        }
        if (url) added.push(url);
      } catch (err) { toast.error(err?.response?.data?.detail || `Upload failed for ${file.name}`); }
    }
    if (added.length) { onChange([...(images || []), ...added]); toast.success(`${added.length} ${label} added`); }
    setBusy(false);
  };
  const del = (i) => onChange(rows.filter((_, x) => x !== i));
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
        <DraggableList items={rows} onChange={onChange}>
          {(src, i, d) => (
            <div key={i} {...d.dropProps} {...(d.count > 1 ? d.handleProps : {})} data-testid={`${testid}-item-${i}`}
              className={`relative group rounded-md overflow-hidden border bg-[#F1F5F4] aspect-video transition-all ${d.count > 1 ? 'cursor-grab active:cursor-grabbing' : ''} ${d.isOver ? 'border-[#00A79D] ring-2 ring-[#00A79D]/40' : 'border-[#E5E7EB]'} ${d.isDragging ? 'opacity-50' : ''}`}>
              <img src={src} alt={`upload ${i + 1}`} draggable={false} className="w-full h-full object-cover pointer-events-none" onError={(e) => { e.target.style.display = 'none'; }} />
              {d.count > 1 && <span className="absolute top-1 left-1 bg-black/50 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-drag-${i}`}><GripVertical className="w-3.5 h-3.5" /></span>}
              <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={() => del(i)} data-testid={`${testid}-remove-${i}`}
                className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </DraggableList>
        <label className="cursor-pointer aspect-video rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] hover:border-[#00A79D] flex flex-col items-center justify-center text-gray-400 hover:text-[#00857D] transition-colors" data-testid={`${testid}-add`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5" /><span className="text-[10px] mt-0.5">Add</span></>}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/heic,image/heif,.heic,.heif" multiple className="hidden" onChange={pick} disabled={busy} />
        </label>
      </div>
      {rows.length > 1 && <p className="text-[11px] text-gray-400 mt-1">Drag images to reorder how they appear in the gallery.</p>}
      {rows.length === 0 && <p className="text-xs text-gray-400 mt-1">No {label} yet - upload a few to build a gallery.</p>}
    </div>
  );
};

export const ClaimListing = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite');
  const [q, setQ] = useState(null);
  const [form, setForm] = useState({ contact_name: '', email: '', plan: 'monthly', addons: [], editor_emails: '', promo_code: '', staff_count: '' });
  const [busy, setBusy] = useState(false);
  const staffN = Number(form.staff_count) || 0;

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/public/claim/quote/${slug}`, { params: staffN ? { staff: staffN } : {} }).then((r) => setQ(r.data)).catch(() => setQ((old) => old || false));
    }, staffN ? 350 : 0);
    return () => clearTimeout(t);
  }, [slug, staffN]);
  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/public/claim/invite/${inviteToken}`).then((r) => {
      if (r.data.email) setForm((f) => ({ ...f, email: f.email || r.data.email }));
    }).catch(() => {});
  }, [inviteToken]);

  const _sub = q ? (form.plan === 'annual' ? q.pricing.annual : q.pricing.monthly) : 0;
  const _add = q ? form.addons.reduce((t, k) => t + (q.addons.find((a) => a.key === k)?.price || 0), 0) : 0;
  const disc = useDiscount(form.promo_code, _sub, _add);
  const [agreed, setAgreed] = useState(false);
  if (q === false) return <Shell><p className="text-gray-600">Supplier not found. <Link to="/browse" className="text-[#00857D] font-semibold">Back to directory</Link></p></Shell>;
  if (!q) return <Shell><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></Shell>;

  const subPrice = form.plan === 'annual' ? q.pricing.annual : q.pricing.monthly;
  const addonTotal = form.addons.reduce((t, k) => t + (q.addons.find((a) => a.key === k)?.price || 0), 0);
  const total = Math.max(0, Math.round((subPrice + addonTotal - disc.discount) * 100) / 100);
  const toggleAddon = (k) => setForm((f) => ({ ...f, addons: f.addons.includes(k) ? f.addons.filter((x) => x !== k) : [...f.addons, k] }));

  const submit = async () => {
    if (!form.contact_name || !form.email) { toast.error('Please add your name and email'); return; }
    if (!staffN) { toast.error('Please enter how many staff are in your team'); return; }
    if (!agreed) { toast.error('Please tick to agree to the Terms of Business'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/claim`, {
        supplier_slug: slug, invite_token: inviteToken, ...form, staff_count: staffN,
        editor_emails: (form.editor_emails || '').split(',').map((x) => x.trim()).filter((x) => x.includes('@')),
      });
      try { const csid = localStorage.getItem('ctc_sid'); if (csid) axios.post(`${API}/public/track/conversion`, { sid: csid, type: 'claim_started' }).catch(() => {}); } catch (e) { /* ignore */ }
      const co = await axios.post(`${API}/public/claim/${data.claim_id}/checkout`, { terms_accepted: true });
      if (co.data.free && co.data.set_password_token) {
        toast.success('Discount applied - no payment needed');
        window.location.href = `/claim/set-password?token=${co.data.set_password_token}`;
        return;
      }
      window.location.href = co.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };

  return (
    <Shell>
      <Link to={`/suppliers/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00857D] mb-4" data-testid="claim-back"><ArrowLeft className="w-4 h-4" /> Back to listing</Link>
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Claim & get Confirmed</span>
      <h1 className="font-heading text-3xl font-extrabold text-gray-900">Claim {q.supplier.name}</h1>
      <p className="text-gray-600 mt-2">{q.sales_call ? 'Add your details and we\'ll set you up with a package that fits.' : 'Confirm your details, choose your plan and any add-ons. You\'ll set a password after payment to build your listing.'}</p>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 mt-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-gray-700">Your name</label>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="claim-name" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" /></div>
          <div><label className="text-sm font-medium text-gray-700">Work email</label>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="claim-email" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" /></div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">How many staff are in your team? <span className="text-gray-400 font-normal">· this sets your price</span></label>
          <StaffCountInput value={form.staff_count} onChange={(v) => setForm({ ...form, staff_count: v })} testid="claim-staff" className="mt-1"
            band={staffN ? { label: q.pricing.band_label, staff_label: q.pricing.staff_label, monthly: q.pricing.monthly, call_only: !!q.sales_call } : null} />
        </div>
        {!staffN ? <p className="text-sm text-gray-500 bg-[#F4F7F9] rounded-lg px-4 py-3" data-testid="claim-staff-needed">Enter your staff number to see your plan and price.</p>
        : q.sales_call ? <SalesCallCard gate={q.sales_call} company={q.supplier.name} email={form.email} name={form.contact_name} staffCount={staffN} band={q.pricing.band} supplierSlug={slug} source="claim_large" testid="claim-sales-call" /> : (<>

        <div>
          <label className="text-sm font-medium text-gray-700">Confirmed plan · {q.pricing.band_label}</label>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {[['monthly', `£${q.pricing.monthly}`, 'per month'], ['annual', `£${q.pricing.annual}`, 'per year · 1 month free']].map(([p, price, sub]) => (
              <button key={p} onClick={() => setForm({ ...form, plan: p })} data-testid={`claim-plan-${p}`}
                className={`text-left border rounded-lg px-4 py-3 transition-colors ${form.plan === p ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB]'}`}>
                <div className="font-heading font-bold text-gray-900">{price} <span className="text-xs font-normal text-gray-500">{sub}</span></div>
                <div className="text-xs text-gray-500 capitalize">{p}</div>
              </button>
            ))}
          </div>
        </div>

        {q.addons.length > 0 && <div>
          <label className="text-sm font-medium text-gray-700">Optional add-ons <span className="text-gray-400 font-normal">· one-off, charged once</span></label>
          <div className="mt-2 space-y-2">
            {q.addons.map((a) => (
              <label key={a.key} className="flex items-center gap-3 border border-[#E5E7EB] rounded-lg px-4 py-2.5 cursor-pointer" data-testid={`claim-addon-${a.key}`}>
                <input type="checkbox" checked={form.addons.includes(a.key)} onChange={() => toggleAddon(a.key)} className="accent-[#00A79D] w-4 h-4" />
                <span className="text-sm text-gray-700 flex-1">{a.label}</span>
                <span className="text-sm font-semibold text-gray-900">£{a.price}</span>
              </label>
            ))}
          </div>
        </div>}

        <div>
          <label className="text-sm font-medium text-gray-700">Team access <span className="text-gray-400 font-normal">· optional, payers only</span></label>
          <input value={form.editor_emails} onChange={(e) => setForm({ ...form, editor_emails: e.target.value })} data-testid="claim-editor-emails" placeholder="colleague@company.com, another@company.com" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
          <p className="text-xs text-gray-400 mt-1">Comma-separate colleagues who should also be able to edit this listing. They'll get a link to set their own password.</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Discount code <span className="text-gray-400 font-normal">· optional</span></label>
          <input value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} data-testid="claim-promo-code" placeholder="Enter a code if you have one" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm uppercase" />
          <DiscountNote d={disc} testid="claim-discount" />
        </div>

        <div className="border-t border-[#E5E7EB] pt-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-500">Total due today</div>
            <div className="font-heading text-2xl font-extrabold text-gray-900" data-testid="claim-total">£{total}</div>
            <div className="text-xs text-gray-500 mt-0.5" data-testid="claim-breakdown">
              Then £{subPrice}/{form.plan === 'annual' ? 'yr' : 'mo'} for the Confirmed subscription{addonTotal > 0 ? ` · £${addonTotal} add-ons charged once` : ''}
            </div>
          </div>
          <TermsTick checked={agreed} onChange={setAgreed} testid="claim-terms" />
          <button onClick={submit} disabled={busy} data-testid="claim-checkout" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
        </>)}
      </div>
    </Shell>
  );
};

/* ---------------------------------------- Payment success -> poll -> set password */
export const ClaimSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('Confirming your payment…');
  const sid = params.get('session_id');

  useEffect(() => {
    if (!sid) { setMsg('Missing payment reference.'); return; }
    let tries = 0; let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await axios.get(`${API}/public/claim/status/${sid}`);
        if (data.payment_status === 'paid' && data.set_password_token) {
          try { const csid = localStorage.getItem('ctc_sid'); if (csid) axios.post(`${API}/public/track/conversion`, { sid: csid, type: 'claim' }).catch(() => {}); } catch (e) { /* ignore */ }
          navigate(`/claim/set-password?token=${data.set_password_token}`); return;
        }
      } catch {}
      if (++tries > 20) { setMsg('Still processing. Please check your email for your setup link.'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sid, navigate]);

  return <Shell><div className="text-center py-16" data-testid="claim-success"><Loader2 className="w-7 h-7 animate-spin text-[#00A79D] mx-auto" /><p className="text-gray-600 mt-4">{msg}</p></div></Shell>;
};

/* ---------------------------------------- Set password */
export const SetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState(''); const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { toast.error('Use at least 8 characters'); return; }
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/claim/set-password`, { token, password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success('Account created');
      navigate('/portal');
    } catch (e) { toast.error(e.response?.data?.detail || 'Link expired'); setBusy(false); }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto bg-white border border-[#E5E7EB] rounded-xl p-7" data-testid="set-password">
        <h1 className="font-heading text-2xl font-extrabold text-gray-900">Set your password</h1>
        <p className="text-gray-600 mt-1 text-sm">Payment received. Create a password to build your listing.</p>
        <input type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="sp-password" className="w-full mt-4 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
        <input type="password" placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="sp-confirm" className="w-full mt-3 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
        <button onClick={submit} disabled={busy} data-testid="sp-submit" className="w-full mt-4 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold py-2.5 rounded-md disabled:opacity-60">{busy ? 'Creating…' : 'Create account & continue'}</button>
      </div>
    </Shell>
  );
};

/* ---------------------------------------- Supplier portal: build listing */
const authApi = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
const STATUS = { draft: ['Draft', 'bg-gray-100 text-gray-700'], in_review: ['In review by CTC', 'bg-amber-100 text-amber-800'], live: ['Live', 'bg-[#00A79D]/15 text-[#00857D]'] };

const SupplierPortalLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password: pw });
      if (data.user?.role !== 'supplier') { setErr('This is a supplier login. Please use your supplier account.'); setBusy(false); return; }
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin();
    } catch (e) { setErr(e.response?.data?.detail || 'Invalid email or password.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto bg-white border border-[#E5E7EB] rounded-xl p-7" data-testid="supplier-portal-login">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#00857D] bg-[#00A79D]/10 px-2.5 py-1 rounded-full mb-3"><Lock className="w-3.5 h-3.5" /> Supplier portal</div>
      <h1 className="font-heading text-2xl font-extrabold text-gray-900">Sign in to your listing</h1>
      <p className="text-gray-500 mt-1 text-sm">Manage your supplier profile and complete your data check.</p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="supplier-login-email"
          className="w-full border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" required />
        <input type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="supplier-login-password"
          className="w-full border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" required />
        {err && <p className="text-sm text-red-600" data-testid="supplier-login-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="supplier-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in
        </button>
      </form>
      <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-center">
        <a href="/forgot-password" className="text-sm font-semibold text-[#00857D] hover:underline" data-testid="supplier-forgot-password">
          Set or reset your password
        </a>
        <p className="text-xs text-gray-400 mt-1">First time signing in, or your temporary password isn't working? Reset it here - we'll email you a link.</p>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-center">
        <a href="/login" className="text-sm text-gray-500 hover:text-gray-800 hover:underline" data-testid="portal-staff-crm-login">
          Care Tech Compare staff? Sign in to the CRM →
        </a>
      </div>
    </div>
  );
};

export const SupplierPortal = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [verify, setVerify] = useState(null);
  const [needLogin, setNeedLogin] = useState(!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState('listing');
  const [saving, setSaving] = useState(false);
  const [newEditor, setNewEditor] = useState('');
  const [obDismissed, setObDismissed] = useState(false);
  const [meta, setMeta] = useState(null);
  const [insights, setInsights] = useState(null);
  useEffect(() => { axios.get(`${API}/public/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const loadInsights = useCallback(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    authApi().get('/supplier-listing/category-insights').then((r) => setInsights(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadInsights(); }, [loadInsights]);

  // Live view ticker - gently increments the demo's view count during a pitch so the
  // analytics feel alive on screen. Purely visual (demo accounts only, not persisted).
  const [viewBump, setViewBump] = useState(0);
  useEffect(() => {
    if (!data?.is_demo) return undefined;
    const id = setInterval(() => setViewBump((b) => b + 1 + Math.floor(Math.random() * 2)), 9000);
    return () => clearInterval(id);
  }, [data?.is_demo]);

  const [resetting, setResetting] = useState(false);
  const resetDemo = async () => {
    setResetting(true);
    try {
      await authApi().post('/supplier-listing/demo/reset');
      setViewBump(0);
      await load();
      toast.success('Demo data reset to a clean, pristine state');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally { setResetting(false); }
  };


  const dismissOnboarding = async () => {
    setObDismissed(true);
    try { await authApi().post('/supplier-listing/onboarding/dismiss'); } catch { /* non-blocking */ }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await authApi().get('/supplier-listing/me'); setData(data); setNeedLogin(false);
      try { const { data: v } = await authApi().get('/supplier-verify/status'); setVerify(v); } catch { setVerify({ verified: true }); }
    }
    catch { localStorage.removeItem(TOKEN_KEY); setNeedLogin(true); }
  }, []);
  useEffect(() => { if (localStorage.getItem(TOKEN_KEY)) load(); }, [load]);

  // The guided demo tour drives the data-check → quote → pay funnel via API; this lets it
  // ask the portal to refetch (show the quote, then flip to paid) without a full reload.
  useEffect(() => {
    const h = () => { if (localStorage.getItem(TOKEN_KEY)) load(); };
    window.addEventListener('ctc-demo-refetch', h);
    return () => window.removeEventListener('ctc-demo-refetch', h);
  }, [load]);

  // Return from a £200 product-unlock Stripe checkout: poll status, then refresh.
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('unlock');
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data: s } = await authApi().get(`/supplier-listing/product-unlock/status/${sid}`);
        if (s.payment_status === 'paid') {
          toast.success('Product published - it is now live in the directory');
          window.history.replaceState({}, '', '/portal'); load(); return;
        }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 1500); else window.history.replaceState({}, '', '/portal');
    };
    poll();
  }, [load]);

  // Return from a confirmed-supplier subscription Stripe checkout.
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('sub');
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data: s } = await authApi().get(`/supplier-verify/quote/status/${sid}`);
        if (s.paid) { toast.success('Subscription active - you are now a confirmed supplier'); window.history.replaceState({}, '', '/portal'); load(); return; }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 1500); else window.history.replaceState({}, '', '/portal');
    };
    poll();
  }, [load]);

  if (needLogin) return <Shell><SupplierPortalLogin onLogin={() => load()} /></Shell>;
  if (!data) return <Shell><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></Shell>;
  if (verify && !verify.verified) return <Shell><SupplierDataCheck status={verify} onChange={setVerify} /></Shell>;
  const L = data.listing; const st = STATUS[data.status] || STATUS.draft;
  const shareListing = async () => {
    const url = `${window.location.origin}/suppliers/${data.supplier.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success('Public link copied to clipboard'); }
    catch { toast.message('Your public link', { description: url }); }
    window.open(`${url}?demo=1`, '_blank', 'noopener');
  };
  const setL = (patch) => setData({ ...data, listing: { ...data.listing, ...patch } });
  const setProduct = (i, patch) => { const products = [...L.products]; products[i] = { ...products[i], ...patch }; setL({ products }); };

  const saveListing = async () => {
    setSaving(true);
    try {
      await authApi().put('/supplier-listing/listing', {
        tagline: L.tagline, description: L.description, logo_url: L.logo_url,
        website: L.website, phone: L.phone,
        categories: L.categories, care_types: L.care_types, resident_types: L.resident_types, specialisms: L.specialisms || [],
        products: L.products, videos: L.videos || [], accordions: L.accordions || [],
        faqs: L.faqs || [], testimonials: L.testimonials || [], screenshots: L.screenshots || [],
      });
      await authApi().put('/supplier-listing/addons', { addon_content: data.addon_content || {} });
      toast.success('Saved');
      loadInsights();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const submit = async () => { await saveListing(); try { await authApi().post('/supplier-listing/submit'); toast.success('Submitted for review'); load(); } catch { toast.error('Submit failed'); } };
  const csv = (arr) => (arr || []).join(', ');

  const requestAssurance = async () => {
    try {
      await authApi().post('/supplier-listing/assurance-request', { note: '' });
      toast.success('Request logged - our team will be in touch about independent assurance.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send request'); }
  };

  const setPrimary = (i) => setL({ products: L.products.map((p, x) => ({ ...p, primary: x === i })) });
  const setFeatured = (i) => setL({ products: L.products.map((p, x) => ({ ...p, featured: x === i ? !p.featured : false })) });
  const unlockProduct = async (pr) => {
    if (!pr.slug) { toast.error('Save your draft first, then publish this product'); return; }
    try {
      const { data: r } = await authApi().post('/supplier-listing/product-unlock/checkout', { product_slug: pr.slug });
      window.location.href = r.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); }
  };

  const aiDraftContent = async (i) => {
    try {
      const { data } = await authApi().post('/supplier-listing/ai-draft-content', { product_index: i });
      const rc = data.rich_content || {};
      const lf = data.listing || {};
      setData((prev) => {
        const products = [...((prev.listing || {}).products || [])];
        products[i] = { ...products[i], rich_content: { ...(products[i].rich_content || {}), ...rc } };
        return { ...prev, listing: {
          ...prev.listing,
          description: lf.description || prev.listing.description,
          tagline: lf.tagline || prev.listing.tagline,
          categories: (lf.categories && lf.categories.length) ? lf.categories : prev.listing.categories,
          products,
        } };
      });
      toast.success('Draft ready - summary, tagline, categories and page content filled. Review and Save.');
      return null;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not draft content');
      return null;
    }
  };


  const addEditor = async () => {
    const email = newEditor.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    try {
      const { data: r } = await authApi().post('/supplier-listing/editors', { email });
      toast.success('Editor added');
      if (r.set_password_url) { try { await navigator.clipboard.writeText(r.set_password_url); toast.message('Set-password link copied - share it with them'); } catch {} }
      setNewEditor(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add editor'); }
  };
  const removeEditor = async (email) => {
    try { await authApi().delete(`/supplier-listing/editors/${encodeURIComponent(email)}`); toast.success('Editor removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove editor'); }
  };

  return (
    <Shell>
      <div className="flex items-center justify-between gap-3" data-testid="supplier-portal">
        <div><h1 className="font-heading text-2xl font-extrabold text-gray-900">{data.supplier.name}</h1>
          <p className="text-sm text-gray-500" data-testid="portal-tier">{data.tier_label || 'Unverified'}{data.band_label ? ` · ${data.band_label}` : ''}{data.plan && data.plan !== 'admin' ? ` · ${data.plan}` : ''}</p>
          {data.is_paid && data.subscription_expires_at && (
            <p className="text-xs text-gray-400 mt-0.5" data-testid="portal-subscription-expiry">Subscription active until {new Date(data.subscription_expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
          )}</div>
        <div className="flex items-center gap-2 shrink-0">
          {data.is_demo && (
            <button onClick={resetDemo} disabled={resetting} data-testid="portal-reset-demo"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
              title="Restore the demo listing to a clean, pristine state">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reset demo
            </button>
          )}
          <button onClick={shareListing} data-testid="portal-share-listing"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00857D] bg-[#00A79D]/10 hover:bg-[#00A79D]/20 border border-[#00A79D]/30 px-3 py-1.5 rounded-md transition-colors" title="Copy your public link and open a preview">
            <Share2 className="w-4 h-4" /> Share listing
          </button>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${st[1]}`} data-testid="portal-status">{st[0]}</span>
        </div>
      </div>

      {verify && verify.quote && <QuoteBanner quote={verify.quote} onAccept={load} isDemo={verify.is_demo} />}
      {verify && verify.product_research && <ResearchReport productResearch={verify.product_research} products={verify.products} />}
      {verify && verify.partner_invited && <PartnerInvite status={verify} questions={verify.partner_questions} onSubmit={load} />}

      {data.supplier.slug === 'care-tech-demo-co' && (
        <button
          onClick={() => { sessionStorage.setItem('ctc_demo_tour', 'public'); window.location.assign('/suppliers/care-tech-demo-co'); }}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00857D] bg-[#00A79D]/10 hover:bg-[#00A79D]/20 border border-[#00A79D]/30 px-3 py-1.5 rounded-md transition-colors"
          data-testid="portal-replay-demo">
          <PlayCircle className="w-3.5 h-3.5" /> Replay demo tour
        </button>
      )}

      {data.status === 'in_review' && <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">Your listing is with the care tech compare team for review. We'll email you when it goes live.</div>}
      {data.status === 'live' && <div className="mt-4 bg-[#00A79D]/10 border border-[#00A79D]/30 text-[#00857D] text-sm rounded-lg px-4 py-3">🎉 Your listing is live. <Link to={`/suppliers/${data.supplier.slug}?demo=1`} className="font-semibold underline">View it</Link></div>}

      {/* Search-reach FOMO nudge for free tiers (Unverified & Verified) */}
      {data.reach && data.reach.capped && (data.reach.categories.total > data.reach.categories.shown || data.reach.settings.total > data.reach.settings.shown) && (
        <div className="mt-4 bg-gradient-to-r from-[#1A3A5F] to-[#00857D] text-white rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="portal-reach-fomo">
          <div className="flex-1">
            <p className="font-semibold">You're only showing in {data.reach.categories.shown} of {data.reach.categories.total} categories.</p>
            <p className="text-sm text-white/85 mt-0.5">
              {data.reach.categories.total - data.reach.categories.shown} product {data.reach.categories.total - data.reach.categories.shown === 1 ? 'category is' : 'categories are'} hidden from buyers on the free plan
              {data.reach.settings.total > data.reach.settings.shown ? `, plus ${data.reach.settings.total - data.reach.settings.shown} care setting${data.reach.settings.total - data.reach.settings.shown === 1 ? '' : 's'}` : ''}. Upgrade to appear everywhere you compete.
            </p>
          </div>
          <a href="mailto:hello@caretechcompare.co.uk?subject=Upgrade%20my%20listing" className="shrink-0 inline-flex items-center gap-1.5 bg-white text-[#1A3A5F] font-semibold text-sm px-4 py-2 rounded-md hover:bg-white/90" data-testid="portal-reach-upgrade">Upgrade my listing <ArrowRight className="w-4 h-4" /></a>
        </div>
      )}

      {/* Private analytics - the supplier's own page views (not public) */}
      {data.analytics && (
        <div className="grid sm:grid-cols-[200px_1fr] gap-4 mt-4" data-testid="portal-analytics">
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Your listing views</p>
            <p className="font-heading text-3xl font-extrabold text-gray-900 mt-1" data-testid="portal-views-total">{(data.analytics.total_views + viewBump).toLocaleString()}</p>
            <p className="text-xs text-gray-500">all-time · {(data.analytics.views_30d + viewBump).toLocaleString()} in the last 30 days</p>
            <p className="text-[11px] text-gray-400 mt-2">Private to you - never shown publicly.</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Last 14 days</p>
            <div className="flex items-end gap-1 h-20">
              {data.analytics.series_14d.map((d, di) => {
                const seriesLive = data.analytics.series_14d.map((x, xi) => xi === data.analytics.series_14d.length - 1 ? x.views + viewBump : x.views);
                const max = Math.max(1, ...seriesLive);
                const val = seriesLive[di];
                return <div key={d.date} title={`${d.date}: ${val}`} className="flex-1 bg-[#00A79D]/70 rounded-t transition-all duration-700" style={{ height: `${Math.max(4, (val / max) * 100)}%` }} data-testid={`portal-bar-${d.date}`} />;
              })}
            </div>
          </div>
        </div>
      )}

      <PortalReviewCard api={authApi} />

      {/* Plan & upgrade */}
      <div className="mt-4 bg-white border border-[#E5E7EB] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="portal-plan">
        <div className="flex-1">
          <p className="text-sm text-gray-500">Your plan</p>
          <p className="font-semibold text-gray-900">{data.tier_label || 'Dormant'}{data.band_label ? ` · ${data.band_label}` : ''}</p>
        </div>
        {data.tier === 'partner' && (
          data.assurance_requested
            ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-md" data-testid="portal-assurance-requested"><Check className="w-4 h-4" /> Assurance requested</span>
            : <button onClick={requestAssurance} className="inline-flex items-center gap-1.5 border border-[#F5871F] text-[#F5871F] hover:bg-[#F5871F]/5 font-semibold text-sm px-4 py-2 rounded-md" data-testid="portal-request-assurance">Apply for independent assurance (Care City) <ShieldCheck className="w-4 h-4" /></button>
        )}
        {data.tier === 'confirmed' && (
          <a href="mailto:hello@caretechcompare.co.uk?subject=Upgrade%20to%20Partner" className="inline-flex items-center gap-1.5 border border-[#1A3A5F] text-[#1A3A5F] hover:bg-[#1A3A5F]/5 font-semibold text-sm px-4 py-2 rounded-md" data-testid="portal-upgrade-partner-tier">Become a Partner <ArrowRight className="w-4 h-4" /></a>
        )}
        {data.reach && data.reach.capped && (
          <Link to={`/claim/${data.supplier.slug}`} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold text-sm px-4 py-2 rounded-md" data-testid="portal-upgrade-partner">Claim &amp; get Confirmed <ArrowRight className="w-4 h-4" /></Link>
        )}
      </div>

      {/* Team access - payers only, owner manages */}
      {data.is_paid && data.is_owner && (
        <div className="mt-4 bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid="portal-team">
          <p className="font-semibold text-gray-900">Team access</p>
          <p className="text-sm text-gray-500 mt-0.5">Add colleagues who can edit this listing. They'll get a link to set their own password.</p>
          <div className="flex gap-2 mt-3">
            <input value={newEditor} onChange={(e) => setNewEditor(e.target.value)} placeholder="colleague@company.com" data-testid="portal-editor-email" className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
            <button onClick={addEditor} data-testid="portal-editor-add" className="bg-[#1A3A5F] hover:bg-[#12293f] text-white font-semibold text-sm px-4 py-2 rounded-md">Add editor</button>
          </div>
          <div className="mt-3 space-y-2">
            {(data.editors || []).length === 0 && <p className="text-sm text-gray-400">No additional editors yet.</p>}
            {(data.editors || []).map((em) => (
              <div key={em} className="flex items-center justify-between border border-[#F1F1F1] rounded-md px-3 py-2" data-testid={`portal-editor-${em}`}>
                <span className="text-sm text-gray-700">{em}</span>
                <button onClick={() => removeEditor(em)} className="text-gray-400 hover:text-red-500" data-testid={`portal-editor-remove-${em}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && !data.onboarding_dismissed && !obDismissed && (
        <OnboardingGuide listing={L} onJump={(t) => setTab(t)} onDismiss={dismissOnboarding} />
      )}

      <CompletenessMeter listing={L} onJump={(t) => setTab(t)} />

      <div className="flex gap-2 mt-6 border-b border-[#E5E7EB]">
        <button onClick={() => setTab('listing')} data-testid="portal-tab-listing" className={`px-4 py-2 text-sm font-semibold ${tab === 'listing' ? 'text-[#00857D] border-b-2 border-[#00A79D]' : 'text-gray-500'}`}>Listing & products</button>
        {data.addons.length > 0 && <button onClick={() => setTab('addons')} data-testid="portal-tab-addons" className={`px-4 py-2 text-sm font-semibold ${tab === 'addons' ? 'text-[#00857D] border-b-2 border-[#00A79D]' : 'text-gray-500'}`}>Add-on content ({data.addons.length})</button>}
        <button onClick={() => setTab('rich')} data-testid="portal-tab-rich" className={`px-4 py-2 text-sm font-semibold ${tab === 'rich' ? 'text-[#00857D] border-b-2 border-[#00A79D]' : 'text-gray-500'}`}>Rich content</button>
      </div>

      {tab === 'rich' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 mt-5 space-y-7" data-testid="portal-rich">
          <p className="text-sm text-gray-500 -mt-1">Add images, videos, FAQs, accordions and testimonials to make your listing stand out.</p>
          <div>
            <label className="text-sm font-semibold text-gray-800">Image gallery / screenshots</label>
            <p className="text-xs text-gray-400 mb-2">Upload screenshots or photos - shown as a click-to-enlarge gallery on your public page.</p>
            <GalleryUpload images={L.screenshots || []} onChange={(screenshots) => setL({ screenshots })} testid="listing-gallery" label="images" />
          </div>
          <RichList label="Videos" testid="videos" rows={L.videos || []} fields={[['title', 'Video title'], ['url', 'YouTube/Vimeo URL']]} onChange={(v) => setL({ videos: v })} />
          <RichList label="Information accordions" testid="accordions" rows={L.accordions || []} fields={[['heading', 'Heading'], ['body', 'Body', true]]} onChange={(v) => setL({ accordions: v })} />
          <RichList label="FAQs" testid="faqs" rows={L.faqs || []} fields={[['question', 'Question'], ['answer', 'Answer', true]]} onChange={(v) => setL({ faqs: v })} />
          <RichList label="Testimonials & quotes" testid="testimonials" rows={L.testimonials || []} fields={[['quote', 'Quote', true], ['author', 'Author'], ['role', 'Role / organisation']]} onChange={(v) => setL({ testimonials: v })} />
        </div>
      )}

      {tab === 'listing' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 mt-5 space-y-4">
          <div><label className="text-sm font-medium text-gray-700">Tagline</label>
            <input value={L.tagline} onChange={(e) => setL({ tagline: e.target.value })} data-testid="portal-tagline" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" /></div>
          <div><label className="text-sm font-medium text-gray-700">About your company</label>
            <textarea rows={5} value={L.description} onChange={(e) => setL({ description: e.target.value })} data-testid="portal-description" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-gray-700">Website URL</label>
              <input type="url" value={L.website || ''} onChange={(e) => setL({ website: e.target.value })} data-testid="portal-website" placeholder="https://www.example.co.uk" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
              <p className="text-[11px] text-gray-400 mt-1">Shown as the “Visit website” link on your public page.</p></div>
            <div><label className="text-sm font-medium text-gray-700">Phone number</label>
              <input type="tel" value={L.phone || ''} onChange={(e) => setL({ phone: e.target.value })} data-testid="portal-phone" placeholder="e.g. 0161 123 4567" className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Logo</label>
              <div className="mt-1"><ImageUpload value={L.logo_url} onChange={(url) => setL({ logo_url: url })} label="logo" testid="portal-logo-upload" aspect="aspect-square" kind="logo" /></div>
              <input value={L.logo_url || ''} onChange={(e) => setL({ logo_url: e.target.value })} placeholder="…or paste a logo URL" data-testid="portal-logo" className="w-full mt-2 border border-[#E5E7EB] rounded-md px-3 py-2 text-xs text-gray-500" /></div>
            <div><label className="text-sm font-medium text-gray-700">Categories</label>
              <RankedPicker testid="portal-categories" options={meta?.categories || []} items={L.categories} onChange={(v) => setL({ categories: v })} rankMap={insights?.rank?.categories || {}} suggestions={insights?.suggestions?.categories || []} max={3} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-gray-700">Care types</label>
              <RankedPicker testid="portal-caretypes" options={meta?.care_types || []} items={L.care_types} onChange={(v) => setL({ care_types: v })} rankMap={insights?.rank?.care_types || {}} suggestions={insights?.suggestions?.care_types || []} max={3} /></div>
            <div><label className="text-sm font-medium text-gray-700">Resident types</label>
              <RankedPicker testid="portal-residenttypes" options={meta?.resident_types || []} items={L.resident_types} onChange={(v) => setL({ resident_types: v })} rankMap={insights?.rank?.resident_types || {}} suggestions={insights?.suggestions?.resident_types || []} max={3} /></div>
            <div><label className="text-sm font-medium text-gray-700">Specialisms</label>
              <RankedPicker testid="portal-specialisms" options={meta?.specialisms || []} items={L.specialisms || []} onChange={(v) => setL({ specialisms: v })} max={3} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between"><label className="text-sm font-medium text-gray-700">Products</label>
              <button onClick={() => setL({ products: [...L.products, { name: '', description: '' }] })} data-testid="portal-add-product" className="inline-flex items-center gap-1 text-sm text-[#00857D] font-semibold"><Plus className="w-4 h-4" /> Add product</button></div>
            <p className="text-xs text-gray-400 mt-1">Your plan includes one <strong>primary</strong> product listing free. Each additional product is a one-off £200 + VAT to publish.</p>
            <div className="space-y-3 mt-2">
              {L.products.length > 1 && <p className="text-[11px] text-gray-400">Drag <GripVertical className="w-3 h-3 inline -mt-0.5" /> to reorder - the order here is the order shown on your public page.</p>}
              <DraggableList items={L.products} onChange={(products) => setL({ products })}>
                {(pr, i, d) => {
                const locked = pr.editable_by_customer === false;
                return (
                <div key={i} {...d.dropProps} className={`border rounded-lg p-3 transition-colors ${d.isOver ? 'border-[#00A79D] ring-1 ring-[#00A79D]/30' : locked ? 'border-gray-200 bg-gray-50' : 'border-[#E5E7EB]'} ${d.isDragging ? 'opacity-50' : ''}`} data-testid={`portal-product-${i}`}>
                  {locked && <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 mb-2" data-testid={`portal-product-locked-${i}`}><Lock className="w-3 h-3" /> Locked - contact us to change this product</p>}
                  <div className="flex gap-2 items-center">
                    {d.count > 1 && <DragHandle handleProps={d.handleProps} testid={`portal-product-${i}-drag`} />}
                    <input placeholder="Product name" value={pr.name || ''} disabled={locked} onChange={(e) => setProduct(i, { name: e.target.value })} className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                    {!locked && <button onClick={() => setL({ products: L.products.filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500 px-2"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <textarea rows={2} placeholder="Short description" value={pr.description || ''} disabled={locked} onChange={(e) => setProduct(i, { description: e.target.value })} className="w-full mt-2 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                  <input type="url" placeholder="Product URL (https://…)" value={pr.url || ''} disabled={locked} onChange={(e) => setProduct(i, { url: e.target.value })} data-testid={`portal-product-url-${i}`} className="w-full mt-2 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                  {!locked && (
                    <div className="mt-2">
                      <label className="text-xs font-medium text-gray-600">Product categories <span className="font-normal text-gray-400">- pick up to 3, drag to rank</span></label>
                      <RankedPicker testid={`portal-product-cats-${i}`} max={3} options={meta?.categories || []} items={pr.categories || []} onChange={(v) => setProduct(i, { categories: v })} rankMap={(insights?.product_rank || {})[pr.slug] || {}} />
                    </div>
                  )}
                  {!locked && <div className="mt-2"><ImageUpload value={pr.image} onChange={(url) => setProduct(i, { image: url })} label="screenshot" testid={`portal-product-image-${i}`} /></div>}
                  <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-dashed border-gray-100">
                    <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer" data-testid={`portal-product-primary-${i}`}>
                      <input type="radio" name="primary-product" checked={!!pr.primary} onChange={() => setPrimary(i)} disabled={locked} className="accent-[#00A79D] w-4 h-4" />
                      Primary listing (free)
                    </label>
                    {!pr.primary && (pr.unlocked === true
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`portal-product-unlocked-${i}`}><Check className="w-3 h-3" /> Published · £200 + VAT paid</span>
                      : pr.unlocked === false
                        ? <button onClick={() => unlockProduct(pr)} disabled={locked} className="text-xs font-semibold text-white bg-[#F5871F] hover:bg-[#d9740f] px-3 py-1.5 rounded-md disabled:opacity-50" data-testid={`portal-product-unlock-${i}`}>Publish for £200 + VAT</button>
                        : <span className="text-[11px] font-semibold text-gray-400" data-testid={`portal-product-live-${i}`}>Live</span>)}
                    <button type="button" onClick={() => setFeatured(i)} data-testid={`portal-product-featured-${i}`}
                      className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${pr.featured ? 'text-amber-700 bg-amber-50 border-amber-300' : 'text-gray-400 border-gray-200 hover:text-amber-600 hover:border-amber-300'}`}
                      title="Highlight one product as Featured on your public page">
                      <Star className={`w-3 h-3 ${pr.featured ? 'fill-amber-400 text-amber-500' : ''}`} /> {pr.featured ? 'Featured' : 'Feature this'}
                    </button>
                  </div>
                  {!locked && <ProductRichEditor value={pr.rich_content} onChange={(rc) => setProduct(i, { rich_content: rc })} onDraft={() => aiDraftContent(i)} productName={pr.name} testid={`portal-product-rich-${i}`} />}
                </div>
                );
                }}
              </DraggableList>
              {L.products.length === 0 && <p className="text-sm text-gray-400">No products yet.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === 'addons' && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 mt-5 space-y-5" data-testid="portal-addons">
          {data.addons.map((a) => (
            <div key={a.key}>
              <label className="text-sm font-semibold text-gray-800">{a.label}</label>
              <textarea rows={4} placeholder={`Draft your ${a.label.toLowerCase()} content here…`} value={(data.addon_content || {})[a.key] || ''}
                onChange={(e) => setData({ ...data, addon_content: { ...(data.addon_content || {}), [a.key]: e.target.value } })}
                data-testid={`portal-addon-${a.key}`} className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2.5 text-sm" />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-5">
        <button onClick={saveListing} disabled={saving} data-testid="portal-save" className="border border-[#E5E7EB] bg-white text-gray-700 font-semibold px-5 py-2.5 rounded-md">{saving ? 'Saving…' : 'Save draft'}</button>
        <button onClick={submit} disabled={data.status === 'in_review'} data-testid="portal-submit" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-2.5 rounded-md disabled:opacity-50"><Check className="w-4 h-4" /> Submit for review</button>
      </div>
    </Shell>
  );
};
